import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { formatAmount } from 'foundation/js/utils/numberUtils'
import PersonalFundraisingActiveCard from 'components/PersonalFundraisingActiveCard/PersonalFundraisingActiveCard.vue'
import PersonalFundraisingListItem from 'components/PersonalFundraisingListItem/PersonalFundraisingListItem.vue'

const LABEL_COLLECTION = 'PersonalFundraisingDashboard'

const DEFAULT_LABELS = {
    createCtaLabel: 'Opret ny indsamling',
    editCtaLabel: 'Rediger indsamling',
    moreOptionsLabel: 'Flere muligheder',
    updateTargetLabel: 'Opdater indsamlingsmål',
    qrCodeLabel: 'QR-kode',
    livestreamLabel: 'Livestream',
    remainingLabelTemplate: 'Du er kun {amount} fra at nå dit mål',
    shareCtaLabel: 'Del indsamlingen',
    shareSubtext: 'Del igen og kom tættere på målet',
    collectedAmountLabel: 'Du har indsamlet',
    donorCountTemplate: '{count} personer har doneret',
    linkCopiedLabel: 'Dit link er kopieret!',
    shareOverlayTitle: 'Del indsamlingen i dit netværk',
    shareOverlayDescription:
        'Jo flere der ser din indsamling, jo tættere kommer du på dit mål. Del linket og giv dine venner, familie og netværk en kærlig påmindelse om at donere, før din indsamling slutter.',
    shareNetworkTemplate: 'Del på {network}',
    shareCopyLinkLabel: 'Kopier link',
    shareGoToCollectionLabel: 'Gå til indsamlingen'
}

export default {
    name: 'PersonalFundraisingDashboard',
    components: {
        PersonalFundraisingActiveCard,
        PersonalFundraisingListItem
    },
    props: {
        createUrl: {
            type: String,
            default: '#'
        },
        userProfileId: {
            type: String,
            default: null
        },
        campaignAreaId: {
            type: String,
            default: null
        },
        pageId: {
            type: [String, Number],
            default: null
        },
        collections: {
            type: Array,
            default: null
        },
        isMindegave: {
            type: Boolean,
            default: false
        }
    },
    setup(props) {
        const store = useStore()
        const isLoadingMoreExpired = ref(false)

        const getLabel = (labelKey) =>
            store.getters['labels/getLabel']({
                labelCollection: LABEL_COLLECTION,
                labelKey
            })

        const resolveLabel = (labelKey) =>
            getLabel(labelKey) || DEFAULT_LABELS[labelKey] || ''

        const storeCollections = computed(
            () => store.state.fundraising.dashboardCollections || []
        )

        const hasInlineCollections = computed(() =>
            Array.isArray(props.collections)
        )

        const collections = computed(() => {
            if (hasInlineCollections.value) {
                return props.collections
            }
            return storeCollections.value
        })

        const activeCollections = computed(() =>
            collections.value.filter((item) => item.isActive)
        )

        const expiredCollections = computed(() =>
            collections.value.filter((item) => !item.isActive)
        )

        const hasActiveCollections = computed(
            () => activeCollections.value.length > 0
        )

        const totalAmount = computed(() => {
            if (hasInlineCollections.value) {
                return collections.value.reduce(
                    (sum, item) => sum + (Number(item.amount) || 0),
                    0
                )
            }

            const apiTotal = store.state.fundraising.dashboardTotalAmount
            if (apiTotal != null) {
                return Number(apiTotal) || 0
            }

            return collections.value.reduce(
                (sum, item) => sum + (Number(item.amount) || 0),
                0
            )
        })

        const formattedTotal = computed(() =>
            hasSettled.value ? `${formatAmount(totalAmount.value, 0)} kr.` : ''
        )

        const visibleExpiredCollections = computed(() => expiredCollections.value)

        const hasMoreExpired = computed(() => {
            if (hasInlineCollections.value) {
                return false
            }
            return !!store.state.fundraising.dashboardExpiredHasNextPage
        })

        const hasSettled = ref(false)

        const isLoading = computed(
            () =>
                !hasSettled.value ||
                store.state.fundraising.dashboardCollectionsLoading
        )

        const hasError = computed(
            () => !!store.state.fundraising.dashboardCollectionsError
        )

        const editLabel = computed(() => resolveLabel('editCtaLabel'))
        const moreOptionsLabel = computed(() => resolveLabel('moreOptionsLabel'))
        const updateTargetLabel = computed(() =>
            resolveLabel('updateTargetLabel')
        )
        const qrCodeLabel = computed(() => resolveLabel('qrCodeLabel'))
        const livestreamLabel = computed(() => resolveLabel('livestreamLabel'))
        const remainingLabelTemplate = computed(() =>
            resolveLabel('remainingLabelTemplate')
        )
        const shareSubtext = computed(() => resolveLabel('shareSubtext'))
        const shareCtaLabel = computed(() => resolveLabel('shareCtaLabel'))
        const collectedAmountLabel = computed(() =>
            resolveLabel('collectedAmountLabel')
        )
        const donorCountTemplate = computed(() =>
            resolveLabel('donorCountTemplate')
        )
        const linkCopiedLabel = computed(() => resolveLabel('linkCopiedLabel'))
        const shareOverlayTitle = computed(() => resolveLabel('shareOverlayTitle'))
        const shareOverlayDescription = computed(() =>
            resolveLabel('shareOverlayDescription')
        )
        const shareNetworkTemplate = computed(() =>
            resolveLabel('shareNetworkTemplate')
        )
        const shareCopyLinkLabel = computed(() => resolveLabel('shareCopyLinkLabel'))
        const shareGoToCollectionLabel = computed(() =>
            resolveLabel('shareGoToCollectionLabel')
        )
        const showExpiredList = computed(
            () => !isLoading.value && !hasError.value && expiredCollections.value.length > 0
        )

        const showActiveList = computed(
            () =>
                !isLoading.value &&
                !hasError.value &&
                hasActiveCollections.value
        )

        const showHeroCta = computed(
            () =>
                !isLoading.value &&
                !hasError.value &&
                !hasActiveCollections.value
        )

        const showCreateLink = computed(
            () =>
                !isLoading.value &&
                !hasError.value &&
                hasActiveCollections.value
        )

        const dashboardRequestPayload = () => ({
            userProfileId: props.userProfileId,
            campaignAreaId: props.campaignAreaId,
            pageId: props.pageId
        })

        const showMore = async () => {
            if (hasInlineCollections.value || isLoadingMoreExpired.value) {
                return
            }
            isLoadingMoreExpired.value = true
            try {
                await store.dispatch(
                    'fundraising/loadMoreExpiredDashboardCollections',
                    dashboardRequestPayload()
                )
            } finally {
                isLoadingMoreExpired.value = false
            }
        }

        const retryLoad = () => {
            if (
                !props.userProfileId ||
                (!props.campaignAreaId && !props.pageId) ||
                hasInlineCollections.value
            ) {
                return
            }
            store.dispatch(
                'fundraising/getPersonalFundraisingDashboard',
                dashboardRequestPayload()
            )
        }

        onMounted(() => {
            if (
                !hasInlineCollections.value &&
                props.userProfileId &&
                (props.campaignAreaId || props.pageId)
            ) {
                store
                    .dispatch(
                        'fundraising/getPersonalFundraisingDashboard',
                        dashboardRequestPayload()
                    )
                    .finally(() => {
                        hasSettled.value = true
                    })
                return
            }

            hasSettled.value = true
        })

        return {
            createUrl: computed(() => props.createUrl),
            isLoading,
            hasError,
            formattedTotal,
            editLabel,
            moreOptionsLabel,
            updateTargetLabel,
            qrCodeLabel,
            livestreamLabel,
            remainingLabelTemplate,
            shareSubtext,
            shareCtaLabel,
            collectedAmountLabel,
            donorCountTemplate,
            linkCopiedLabel,
            shareOverlayTitle,
            shareOverlayDescription,
            shareNetworkTemplate,
            shareCopyLinkLabel,
            shareGoToCollectionLabel,
            isMemorial: computed(() => {
                if (hasInlineCollections.value) {
                    return props.isMindegave
                }
                const fromApi = store.state.fundraising.dashboardIsMindegave
                if (typeof fromApi === 'boolean') {
                    return fromApi
                }
                return props.isMindegave
            }),
            showActiveList,
            activeCollections,
            showHeroCta,
            showCreateLink,
            showExpiredList,
            visibleExpiredCollections,
            hasMoreExpired,
            showMore,
            retryLoad
        }
    }
}
